import { useQuery } from '@tanstack/react-query'
import { CommentResponse, CommentsHttpClient } from '@omnea-app/comments/commentsHttpClient'
import { supplierPortalPath } from '@omnea-app/fe-api-client/supplierPortalPath'
import { useAuthenticatedUser, useIsSupplierRole } from '@omnea-app/fe-auth/UserSessionProvider'
import { requestCommentsQueryKey } from '@omnea-app/fe-comments/commentQueryKeys'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { ThreadInfo } from './useRequestComments'

export type RequestCommentsResponse = CommentResponse<'/request-comments/{requestId}', 'get'>
export type RequestCommentsData = NonNullable<RequestCommentsResponse['data']>

interface UseGetRequestCommentOptions<TData = RequestCommentsData> {
  skip?: boolean
  refetchInterval?: number
  select?: (data: RequestCommentsData) => TData
}

export function useGetRequestComment<TData = RequestCommentsData>(
  { requestId, requestFormId, questionId, taskId, rfxId, auctionId }: ThreadInfo,
  options?: UseGetRequestCommentOptions<TData>
) {
  const { getToken } = useAuthenticatedUser()
  const isSupplier = useIsSupplierRole()

  return useQuery<RequestCommentsData, Error, TData>({
    queryKey: [requestCommentsQueryKey, requestId, requestFormId, questionId, taskId, rfxId, auctionId],
    refetchInterval: options?.refetchInterval,
    enabled: !options?.skip,
    select: options?.select,
    queryFn: async () => {
      const apiClient = new CommentsHttpClient({ authorization: await getToken() })
      const { data, error } = await apiClient.GET(supplierPortalPath('/request-comments/{requestId}', isSupplier), {
        params: {
          path: {
            requestId,
          },
          query: {
            requestFormId,
            questionId,
            taskId,
            rfxId,
            auctionId,
          },
        },
      })
      throwIfApiError(error)
      return data
    },
  })
}
